import { useState, useMemo } from 'react'
import { useUser } from '@clerk/clerk-react'
import { Link, NavLink, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import {
  Activity,
  Users,
  Send,
  FileText,
  MessageSquare,
  ShieldOff,
  Filter,
  Sparkles,
  FlaskConical,
  LifeBuoy,
  ArrowLeft,
  ExternalLink,
  Loader2,
  DollarSign,
  AlertTriangle,
  MoreHorizontal,
  Gift,
  Zap,
  Copy,
  Check,
  Mail,
  Search,
  Link2,
  Stethoscope,
  Lightbulb,
  Building2,
  Bot,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ScrollArea } from '@/components/ui/scroll-area'
import { MatrixRain } from '@/components/matrix-rain'
import { AnimatedNumber } from '@/components/animated-number'
import { cn } from '@/lib/utils'
import { canViewAdminConsole, isStaffUser } from './staff'
import { setImpersonatedUser } from '@/lib/impersonation'
import { SkillsLabPage } from './skills-lab'
import { AdminSupportPage } from './support-tickets'
import { useAdminSupportUnread } from './use-admin-support'
import { OrchestratorPage } from './orchestrator-panel'
import { LifecyclePage } from './lifecycle-panel'
import { MessageAuditPage } from './message-audit-panel'
import { MessageInsightsPage } from './message-insights-panel'
import { SeoPage } from './seo-panel'
import { LinksPage } from './links-panel'
import { TeamsPage } from './teams-panel'
import { useAdminApiBase } from './admin-base'
import { CustomPlansPage } from './custom-plans-panel'
import { McpPage } from './mcp-panel'
import {
  useMessageInsightsBadge,
  useAdminOverview,
  useAdminUsers,
  useAdminLeads,
  useAdminCampaigns,
  useAdminContent,
  useAdminInbox,
  useAdminInboxMessages,
  useAdminSpend,
  useToggleComp,
  useFreeMonthCodes,
  useGenerateFreeMonthCode,
  type AdminUserRow,
  type AdminServiceCard,
  type AdminUserFunnel,
  type AdminPerUserDailyRow,
  type AdminOrgSummary,
  type AdminCosts,
  type AdminFixedCost,
} from './use-admin'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { fmtLocale } from '@/lib/dates'
import { PLAN_PRICING } from '@dealcloser/shared'

// Green-only override of the dashboard's brand variables — applied via a
// scoping wrapper class so the rest of the app (cobalt) is untouched.
const GREEN_THEME_STYLE = `
  .super-admin {
    --color-cobalt: #10B981;
    --color-cobalt-bright: #34D399;
    --color-glow-white: #ECFDF5;
  }
  .super-admin a, .super-admin .text-\\[var\\(--color-cobalt-bright\\)\\] {
    color: #34D399;
  }
`

function NotStaff() {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
          <ShieldOff className="h-10 w-10 text-[var(--color-silver)]" />
          <h2 className="text-xl font-semibold text-[var(--color-silver-bright)]">
            Staff only
          </h2>
          <p className="text-sm text-[var(--color-silver)]">
            This dashboard is restricted to DealCloser staff accounts.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-2">
            <Link to="/dashboard">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to dashboard
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

function AdminNav() {
  const { data: supportUnread = 0 } = useAdminSupportUnread()
  const { data: insightsBadge } = useMessageInsightsBadge()
  const newSuggestions = insightsBadge?.new_suggestions ?? 0
  const tabs = [
    { to: '/super-admin', label: 'Overview', icon: Activity, end: true },
    // Support sits right after Overview (2026-09-26): at the end of the bar it
    // scrolled off the edge on most screens, hiding its unread count.
    { to: '/super-admin/support', label: 'Support', icon: LifeBuoy },
    { to: '/super-admin/users', label: 'Users', icon: Users },
    { to: '/super-admin/teams', label: 'Teams', icon: Building2 },
    { to: '/super-admin/leads', label: 'Leads', icon: Sparkles },
    { to: '/super-admin/campaigns', label: 'Campaigns', icon: Send },
    { to: '/super-admin/content', label: 'Content', icon: FileText },
    { to: '/super-admin/inbox', label: 'Inboxes', icon: MessageSquare },
    { to: '/super-admin/orchestrator', label: 'Orchestrator', icon: Zap },
    { to: '/super-admin/message-audit', label: 'Message Audit', icon: Stethoscope },
    { to: '/super-admin/message-insights', label: 'Message Insights', icon: Lightbulb },
    { to: '/super-admin/mcp', label: 'MCP', icon: Bot },
    { to: '/super-admin/lifecycle', label: 'Lifecycle', icon: Mail },
    { to: '/super-admin/seo', label: 'SEO', icon: Search },
    { to: '/super-admin/links', label: 'Links & Payout', icon: Link2 },
    { to: '/super-admin/custom-plans', label: 'Custom Plans', icon: Building2 },
    { to: '/super-admin/spend', label: 'API Spend', icon: DollarSign },
    { to: '/super-admin/skills', label: 'Skills Lab', icon: FlaskConical },
  ]
  // Mobile: single row, horizontally scrollable (swipe sideways; the faded
  // edges hint at more). sm and up: the bar WRAPS, because 15 tabs no longer
  // fit one row and anything past the edge (Support and its unread count)
  // was invisible without scrolling.
  return (
    <div className="relative -mx-6 border-b border-border pb-3 sm:mx-0">
      <div
        className="flex flex-nowrap items-center gap-1 overflow-x-auto overscroll-x-contain px-6 [scrollbar-width:none] sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              cn(
                'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-[rgba(16,185,129,0.12)] text-[#34D399] shadow-[0_0_20px_rgba(16,185,129,0.12)]'
                  : 'text-[var(--color-silver)] hover:bg-[rgba(255,255,255,0.04)] hover:text-[var(--color-silver-bright)]',
              )
            }
          >
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
            {t.to === '/super-admin/support' && supportUnread > 0 && (
              <span
                className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-white"
                aria-label={`${supportUnread} unread support message${supportUnread === 1 ? '' : 's'}`}
              >
                {supportUnread > 99 ? '99+' : supportUnread}
              </span>
            )}
            {t.to === '/super-admin/message-insights' && newSuggestions > 0 && (
              <span
                className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#10B981] px-1 text-[10px] font-semibold leading-none text-white"
                aria-label={`${newSuggestions} SKILL.md suggestion${newSuggestions === 1 ? '' : 's'} to review`}
              >
                {newSuggestions > 99 ? '99+' : newSuggestions}
              </span>
            )}
          </NavLink>
        ))}
      </div>
      {/* Edge fades — only visible on small screens where overflow is real. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-[var(--color-background)] to-transparent sm:hidden"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-[var(--color-background)] to-transparent sm:hidden"
      />
    </div>
  )
}

function StatCard({
  label,
  value,
  icon: Icon,
  trend,
}: {
  label: string
  value: number
  icon: typeof Users
  trend?: string
}) {
  return (
    <Card className="group relative flex-1 overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-[#10B981]/0 blur-2xl transition-all duration-300 group-hover:bg-[#10B981]/15"
      />
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <p className="eyebrow text-[var(--color-silver)]">{label}</p>
          <Icon className="h-4 w-4 text-[#34D399]" />
        </div>
        <div className="mt-1 overflow-clip-y text-2xl font-bold tracking-tight text-[var(--color-silver-bright)]">
          <AnimatedNumber
            value={value}
            format={(n) => Math.round(n).toLocaleString()}
            className="inline-block animate-digit-drop"
          />
        </div>
        {trend && (
          <p className="mt-0.5 text-xs text-[var(--color-silver)]">{trend}</p>
        )}
      </CardContent>
    </Card>
  )
}

// Activated vs Paid. Billing buckets are exclusive (every user in one);
// "activated" = connected LinkedIn + a launched campaign, counted per bucket.
function UserFunnelCard({ f }: { f: AdminUserFunnel }) {
  const rows: Array<{ label: string; n: number; activated: number; hint: string }> = [
    { label: 'Paid', n: f.paid, activated: f.activated_paid, hint: 'Stripe subscription, not comp, free month over' },
    { label: 'Free month', n: f.free_month, activated: f.activated_free_month, hint: '100%-off first month still running' },
    { label: 'Comp', n: f.comp, activated: f.activated_comp, hint: 'Friends and family, never billed' },
    { label: 'Unpaid', n: f.unpaid, activated: f.activated_unpaid, hint: 'No active subscription' },
  ]
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Activated vs Paid</CardTitle>
        <p className="text-[10px] text-[var(--color-silver)]">
          Activated = connected LinkedIn + at least one launched campaign. {f.activated} of {f.total} users activated.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-1.5 text-sm">
          <span className="text-[10px] uppercase tracking-wider text-[var(--color-silver)]">Billing</span>
          <span className="text-[10px] uppercase tracking-wider text-[var(--color-silver)] text-right">Users</span>
          <span className="text-[10px] uppercase tracking-wider text-[var(--color-silver)] text-right">Activated</span>
          {rows.map((r) => (
            <div key={r.label} className="contents">
              <span className="text-[var(--color-silver-bright)]" title={r.hint}>{r.label}</span>
              <span className="font-mono text-right text-[var(--color-silver-bright)]">{r.n}</span>
              <span className="font-mono text-right text-[#34D399]">{r.activated}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

export function OverviewPage() {
  const { data, isLoading } = useAdminOverview()
  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-12 text-[var(--color-silver)]">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading overview…
      </div>
    )
  }
  const c = data.counts
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row">
        <StatCard label="Users" value={c.users} icon={Users} />
        <StatCard label="Connected LinkedIns" value={c.connected_accounts} icon={Activity} />
        <StatCard label="Leads" value={c.leads} icon={Sparkles} />
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <StatCard label="Campaigns" value={c.campaigns} icon={Send} />
        <StatCard label="Posts" value={c.posts} icon={FileText} />
        <StatCard label="Messages" value={c.messages} icon={MessageSquare} />
      </div>

      {data.funnel && <UserFunnelCard f={data.funnel} />}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Plan distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5 text-sm">
              {data.plans.map((p) => (
                <div key={p.plan} className="flex items-center justify-between">
                  <span className="text-[var(--color-silver-bright)] capitalize">{p.plan}</span>
                  <Badge className="bg-[rgba(16,185,129,0.12)] text-[#34D399] border-[rgba(16,185,129,0.25)]">
                    {p.n}
                  </Badge>
                </div>
              ))}
              {typeof data.comp_count === 'number' && data.comp_count > 0 && (
                <div className="flex items-center justify-between pt-1 border-t border-border">
                  <span className="text-[var(--color-silver-bright)] flex items-center gap-1">
                    <Gift className="h-3 w-3 text-[#D8B4FE]" /> Comp (F&amp;F)
                  </span>
                  <Badge className="bg-[rgba(168,85,247,0.14)] text-[#D8B4FE] border-[rgba(168,85,247,0.3)]">
                    {data.comp_count}
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Activity (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            {data.recent_activity_24h.length === 0 ? (
              <p className="text-xs text-[var(--color-silver)]">No activity in the last 24 hours.</p>
            ) : (
              <div className="space-y-1.5 text-sm">
                {data.recent_activity_24h.map((a) => (
                  <div key={a.action_type} className="flex items-center justify-between">
                    <span className="text-[var(--color-silver-bright)] font-mono text-xs">
                      {a.action_type}
                    </span>
                    <Badge className="bg-[rgba(255,255,255,0.05)] text-[var(--color-silver-bright)]">
                      {a.n}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// AI Spend page — live monitor for the @dealcloser/ai-gateway daily cap.
//
// Polls /api/admin/spend every 30s (via useAdminSpend). Shows: today's USD,
// the cap, headroom, a progress bar, breakdown by source (which Anthropic-
// touching feature spent), and top 20 users by spend. A small day picker
// lets staff look at previous days for the same data.
//
// Cap is enforced server-side by the gateway — this page is read-only
// observability. See AI_GATEWAY_GUARDRAIL.md.
// ---------------------------------------------------------------------------

function formatUsd(n: number): string {
  // 4 decimals is overkill for display but the data is fractional-cents,
  // so anything coarser hides small classifier-only spend. Round to 4
  // and let the eye scan the column.
  return `$${n.toFixed(4)}`
}

function SourceLabel({ source }: { source: string }) {
  // Pretty-print the gateway's source enum without losing precision.
  const map: Record<string, string> = {
    reply_classifier: 'Reply Classifier',
    reply_generator: 'Reply Generator',
    engagement_dm: 'Engagement DM',
    simulate: 'Simulator',
    content_gen: 'Content Generation',
    outreach_gen: 'Outreach Generation',
    enrichment: 'Lead Enrichment',
    quality_gate: 'Quality Gate',
    classifier_ai: 'Classifier (AI worker)',
    other: 'Other',
  }
  const label = map[source] ?? source
  return (
    <span className="font-mono text-xs text-[var(--color-silver-bright)]">{label}</span>
  )
}

// ---------------------------------------------------------------------------
// PerUserDailyTable — one row per user with their full per-service spend
// for the target day, side-by-side with their per-user daily cap. Drives
// the "Spend per user (today)" section of the API Spend page added
// 2026-06-07. Sort order: total spend desc (highest first).
// ---------------------------------------------------------------------------
function PerUserDailyTable({
  rows,
  defaultCap,
}: {
  rows: AdminPerUserDailyRow[]
  defaultCap: number
}) {
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-[var(--color-silver)] italic">
          No per-user spend recorded for this day yet.
        </CardContent>
      </Card>
    )
  }
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">API spend per user (this day)</CardTitle>
        <p className="mt-1 text-[10px] text-[var(--color-silver)] leading-snug">
          One row per user; sorted by today's spend. Daily cap is per-user
          (user_settings.daily_ai_spend_cap_usd) and falls back to ${defaultCap.toFixed(2)}
          when unset. The per-user cap is the only ceiling that can halt a
          campaign for the day — no org-wide cap, no Unipile cap. Web searches
          (lead research on Ollama) have their own daily cap, counted in
          searches: user_settings.daily_web_search_cap, default 20.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-[var(--color-silver)] border-b border-border">
              <tr>
                <th className="text-left font-medium px-3 py-2">User</th>
                <th className="text-left font-medium px-2 py-2">Plan</th>
                <th className="text-right font-mono font-medium px-2 py-2">Anthropic</th>
                <th className="text-right font-mono font-medium px-2 py-2">OpenAI</th>
                <th className="text-right font-mono font-medium px-2 py-2">Perplexity</th>
                <th className="text-right font-mono font-medium px-2 py-2">Ollama</th>
                <th className="text-right font-mono font-medium px-2 py-2" title="Ollama web searches used today / daily web search cap">Web searches</th>
                <th className="text-right font-mono font-medium px-2 py-2">Total</th>
                <th className="text-right font-mono font-medium px-3 py-2">Cap</th>
                <th className="text-right font-mono font-medium px-3 py-2">% used</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const tier =
                  r.pct_used < 50 ? 'safe' : r.pct_used < 80 ? 'warn' : 'danger'
                const pctColor =
                  tier === 'safe' ? '#34D399' : tier === 'warn' ? '#FBBF24' : '#F87171'
                return (
                  <tr
                    key={`${r.user_id ?? 'null'}-${idx}`}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="px-3 py-2 align-top">
                      <div className="font-mono text-[var(--color-silver-bright)] truncate max-w-[14rem]">
                        {r.email || (r.user_id?.slice(0, 18) ?? '—')}
                      </div>
                      <div className="font-mono text-[10px] text-[var(--color-silver)] truncate max-w-[14rem]">
                        {r.user_id ?? <span className="italic">unattributed</span>}
                      </div>
                    </td>
                    <td className="px-2 py-2 align-top">
                      <span className="text-[var(--color-silver-bright)]">
                        {/* Comp accounts resolve to Pro everywhere (model, features,
                            limits), so show the EFFECTIVE plan here too — not the raw
                            underlying 'starter'. The [comp] tag stays so staff can still
                            see it's complimentary (and that revoking reverts to r.plan). */}
                        {r.is_comp === 1 ? 'pro' : r.plan}
                        {r.is_comp === 1 && (
                          <span className="ml-1 text-[9px] text-[#FBBF24]">[comp]</span>
                        )}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-[var(--color-silver-bright)] align-top">
                      {r.anthropic_usd > 0 ? formatUsd(r.anthropic_usd) : '—'}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-[var(--color-silver-bright)] align-top">
                      {r.openai_usd > 0 ? formatUsd(r.openai_usd) : '—'}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-[var(--color-silver-bright)] align-top">
                      {r.perplexity_usd > 0 ? formatUsd(r.perplexity_usd) : '—'}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-[var(--color-silver-bright)] align-top">
                      {(r.ollama_usd ?? 0) > 0 ? formatUsd(r.ollama_usd ?? 0) : '—'}
                    </td>
                    <td
                      className="px-2 py-2 text-right font-mono align-top"
                      style={{
                        color:
                          r.web_search_cap !== undefined && r.web_search_cap > 0 && (r.web_searches ?? 0) >= r.web_search_cap
                            ? '#F87171'
                            : 'var(--color-silver-bright)',
                      }}
                    >
                      {r.web_search_cap !== undefined && r.user_id
                        ? `${r.web_searches ?? 0} / ${r.web_search_cap}`
                        : (r.web_searches ?? 0) > 0 ? String(r.web_searches) : '—'}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-[var(--color-silver-bright)] font-bold align-top">
                      {formatUsd(r.total_usd)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[var(--color-silver)] align-top">
                      ${r.daily_cap_usd.toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono align-top" style={{ color: pctColor }}>
                      {r.pct_used}%
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// OrgSummaryCard — single tile showing org-wide grand totals: total spend
// today across all users + all services, and the sum of every user's per-
// user daily cap. Lets staff see at a glance how close we are to the
// org's aggregate allowance. Added 2026-06-07 alongside PerUserDailyTable.
// ---------------------------------------------------------------------------
function OrgSummaryCard({ summary }: { summary: AdminOrgSummary }) {
  const pct = summary.pct_used
  const tier: 'safe' | 'warn' | 'danger' =
    pct < 50 ? 'safe' : pct < 80 ? 'warn' : 'danger'
  const barColor =
    tier === 'safe' ? '#34D399' : tier === 'warn' ? '#FBBF24' : '#F87171'
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-baseline justify-between">
          <CardTitle className="text-sm">Org totals (all users)</CardTitle>
          <div className="font-mono text-sm text-[var(--color-silver-bright)]">
            {formatUsd(summary.total_daily_spend_usd)} / ${summary.total_daily_limit_usd.toFixed(2)}
          </div>
        </div>
        <p className="mt-1 text-[10px] text-[var(--color-silver)] leading-snug">
          Total org limit is the SUM of every user's per-user cap. New Pro
          signups get $0.50/day from the Stripe webhook; users without a
          settings row fall back to ${summary.default_per_user_cap_usd.toFixed(2)}/day.
          {summary.user_count} user{summary.user_count === 1 ? '' : 's'} in
          the system.
        </p>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="h-2 w-full overflow-hidden rounded-full bg-[rgba(255,255,255,0.06)]">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${pct}%`, backgroundColor: barColor }}
          />
        </div>
        <div className="mt-1 flex justify-between text-[10px] text-[var(--color-silver)]">
          <span>{pct}% of org cap used</span>
          <span>{formatUsd(summary.total_daily_headroom_usd)} headroom</span>
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// ServiceCard — per-provider tile on the API Spend page. Shows today's
// USD, the per-user-cap fallback, headroom + progress bar, and the
// top-5 sources + top-3 users for that single service. Used for
// Anthropic / OpenAI / Perplexity.
// ---------------------------------------------------------------------------
function ServiceCard({ card }: { card: AdminServiceCard }) {
  const pct = card.cap_usd > 0
    ? Math.min(100, Math.max(0, (card.today_usd / card.cap_usd) * 100))
    : 0
  // Anthropic has no org cap: the per-user cap is what throttles, so colour
  // by the busiest user's share of their own cap, not by the platform total.
  const perUserCaps = card.cap_basis === 'sum_of_user_caps'
  const tierPct = perUserCaps ? (card.max_user_pct_used ?? 0) : pct
  const tier: 'safe' | 'warn' | 'danger' =
    tierPct < 50 ? 'safe' : tierPct < 80 ? 'warn' : 'danger'
  const barColor =
    tier === 'safe' ? '#34D399' : tier === 'warn' ? '#FBBF24' : '#F87171'

  const label = card.service[0]!.toUpperCase() + card.service.slice(1)
  const capLabel = perUserCaps
    ? 'sum of user caps'
    : card.service === 'anthropic' ? 'per-user fallback' : 'org cap'

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-baseline justify-between">
          <CardTitle className="text-sm">{label}</CardTitle>
          <div className="text-right">
            <div className="font-mono text-sm text-[var(--color-silver-bright)]">
              {formatUsd(card.today_usd)} / ${card.cap_usd.toFixed(2)}
            </div>
            <div className="text-[10px] text-[var(--color-silver)] font-mono">
              {capLabel}
            </div>
          </div>
        </div>
        {card.billing_note && (
          <p className="mt-1 text-[10px] text-[var(--color-silver)] leading-snug">
            {card.billing_note}
          </p>
        )}
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-3">
        <div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-[rgba(255,255,255,0.06)]">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${pct}%`, backgroundColor: barColor }}
            />
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-[var(--color-silver)]">
            <span>{card.pct_used}% used</span>
            <span>{formatUsd(card.headroom_usd)} headroom</span>
          </div>
          {perUserCaps && (
            <p className="mt-1 text-[10px] text-[var(--color-silver)]">
              Highest user: {card.max_user_pct_used ?? 0}% of their cap
              {card.max_user_email ? ` (${card.max_user_email})` : ''}
              {(card.users_at_cap ?? 0) > 0 ? ` · ${card.users_at_cap} at cap` : ''}
            </p>
          )}
        </div>

        {/* Top sources */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[var(--color-silver)] mb-1">By source</p>
          {card.by_source.length === 0 ? (
            <p className="text-xs text-[var(--color-silver)] italic">No calls today.</p>
          ) : (
            <div className="space-y-0.5">
              {card.by_source.slice(0, 5).map((row) => (
                <div key={row.source} className="flex items-center justify-between text-xs">
                  <SourceLabel source={row.source} />
                  <span className="font-mono text-[var(--color-silver-bright)]">
                    {formatUsd(row.usd)} <span className="text-[var(--color-silver)]">({row.calls})</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top users — only render if any */}
        {card.by_user.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[var(--color-silver)] mb-1">Top users</p>
            <div className="space-y-0.5">
              {card.by_user.slice(0, 3).map((row, idx) => (
                <div key={`${row.user_id ?? 'system'}-${idx}`} className="flex items-center justify-between text-xs">
                  <span className="font-mono text-[var(--color-silver-bright)] truncate">
                    {row.user_id?.slice(0, 18) ?? <span className="text-[var(--color-silver)]">cron</span>}
                  </span>
                  <span className="font-mono text-[var(--color-silver-bright)]">
                    {formatUsd(row.usd)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Monthly cost of the tools and APIs the product runs on.
 *
 * Fixed subscriptions are config (packages/shared/src/constants/fixed-costs.ts),
 * not database rows, because they change a few times a year and belong in git
 * where the history of what we paid is tracked. Usage-billed spend is NOT
 * re-counted here: it already appears in the cards below, so adding it twice
 * would overstate the run rate. What IS added is the month-to-date usage
 * projected forward, so a partial month is never compared against a whole one.
 */
function MonthlyCostPanel({ costs }: { costs: AdminCosts }) {
  const usd = (n: number) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })

  const byCategory: Array<{ key: AdminFixedCost['category']; label: string }> = [
    { key: 'infrastructure', label: 'Infrastructure' },
    { key: 'api', label: 'Paid APIs' },
    { key: 'tooling', label: 'Tooling' },
    { key: 'usage-based', label: 'Usage-billed (counted below, not here)' },
  ]

  const margin = costs.gross_margin_usd
  const marginTone =
    margin === null ? 'text-[var(--color-silver)]' : margin >= 0 ? 'text-[#34D399]' : 'text-[#F87171]'

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base text-[var(--color-silver-bright)]">
          <DollarSign className="h-4 w-4 text-[#34D399]" />
          Monthly run rate
          {costs.estimated && (
            <Badge
              variant="outline"
              className="border-none bg-[rgba(251,191,36,0.12)] text-xs text-[#FBBF24]"
              title="Some lines are estimated from public pricing, not confirmed against an invoice."
            >
              estimated · {costs.unverified_count} unconfirmed
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          {[
            { label: 'Subscriptions', value: usd(costs.fixed_monthly_usd), tone: 'text-[var(--color-silver-bright)]' },
            { label: 'AI, projected', value: usd(costs.ai_projected_monthly_usd), tone: 'text-[var(--color-silver-bright)]' },
            { label: 'Total / month', value: usd(costs.total_run_rate_usd), tone: 'text-[#FBBF24]' },
            { label: 'MRR', value: usd(costs.mrr_usd), tone: 'text-[#34D399]' },
            {
              label: margin === null ? 'Margin' : margin >= 0 ? 'Gross margin' : 'Monthly loss',
              value: margin === null ? '—' : usd(Math.abs(margin)),
              tone: marginTone,
            },
          ].map((s) => (
            <div key={s.label} className="rounded-md border border-[var(--color-border)] p-3">
              <p className="eyebrow text-[var(--color-silver)]">{s.label}</p>
              <p className={cn('mt-1 text-xl font-bold tabular-nums', s.tone)}>{s.value}</p>
            </div>
          ))}
        </div>

        <p className="text-xs text-[var(--color-silver)]">
          {costs.paying_users === 0 ? (
            <>
              No paying customers yet, so cost per customer is not meaningful. Every
              dollar here is currently pure burn.
            </>
          ) : (
            <>
              {costs.paying_users} paying customer{costs.paying_users === 1 ? '' : 's'} ·{' '}
              <strong className="text-[var(--color-silver-bright)]">
                {usd(costs.cost_per_paying_user_usd ?? 0)}
              </strong>{' '}
              to serve each one. Basic is ${PLAN_PRICING.starter} and Pro is ${PLAN_PRICING.pro}, so anything above
              that per customer means the plan loses money.
            </>
          )}
        </p>

        <div className="space-y-4">
          {byCategory.map((cat) => {
            const rows = costs.items.filter((i) => i.category === cat.key)
            if (rows.length === 0) return null
            return (
              <div key={cat.key}>
                <p className="eyebrow mb-2 text-[var(--color-silver)]">{cat.label}</p>
                <div className="space-y-1.5">
                  {rows.map((i) => (
                    <div
                      key={i.id}
                      className="flex items-start justify-between gap-4 border-b border-[var(--color-border)] pb-1.5 last:border-0"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[var(--color-silver-bright)]">{i.name}</span>
                          {!i.verified && (
                            <span className="text-[10px] uppercase tracking-wider text-[#FBBF24]">
                              unconfirmed
                            </span>
                          )}
                        </div>
                        <p className="text-xs leading-snug text-[var(--color-silver)]">{i.note}</p>
                      </div>
                      <span className="shrink-0 text-sm tabular-nums text-[var(--color-silver-bright)]">
                        {i.usdPerMonth === 0 ? '—' : usd(i.usdPerMonth)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        <p className="text-xs text-[var(--color-silver)]">
          Edit these in <code className="text-[var(--color-silver-bright)]">packages/shared/src/constants/fixed-costs.ts</code>.
          Put the real figure from each provider's billing page against it and set
          <code className="text-[var(--color-silver-bright)]"> verified: true</code>, and the
          estimated label disappears.
        </p>
      </CardContent>
    </Card>
  )
}

function AISpendPage() {
  // 'YYYY-MM-DD' day-picker. Defaults to undefined (= today) so the hook
  // omits the query param and the worker uses date('now').
  const [day, setDay] = useState<string | null>(null)
  const { data, isLoading, isError, refetch, dataUpdatedAt } = useAdminSpend(day ?? undefined)

  // Day picker bounds: today and the previous 14 days. Beyond two weeks
  // becomes a manual SQL query. MUST be declared BEFORE the early returns
  // below — React's rules-of-hooks require every hook to run in the same
  // order every render. Previously this useMemo sat after the isLoading /
  // isError early returns, so the first render had 2 hooks and the second
  // render had 3, tripping minified React error #310.
  const dayOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string }> = []
    const todayUtc = new Date()
    todayUtc.setUTCHours(0, 0, 0, 0)
    for (let i = 0; i < 15; i++) {
      const d = new Date(todayUtc)
      d.setUTCDate(d.getUTCDate() - i)
      const iso = d.toISOString().slice(0, 10)
      opts.push({ value: iso, label: i === 0 ? `Today (${iso})` : iso })
    }
    return opts
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-[var(--color-silver)]">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading spend report…
      </div>
    )
  }
  if (isError || !data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-[var(--color-silver)]">
          Couldn't load API spend. Refresh or check that the leads worker is
          reachable.
          <div className="mt-3">
            <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Backward-compat: older deploys returned only the Claude-only shape
  // (today_usd/cap_usd/by_source/by_user without `services`/`apollo`/`total_*`).
  // If we're rendering against that old API response, synthesise a single-
  // service "anthropic" card so the new layout still renders something
  // useful. The worker rolls out the new shape additively, so this branch
  // is transient and will be hit only during the deploy window.
  const services = data.services ?? [
    {
      service: 'anthropic' as const,
      throttle_kind: 'usd' as const,
      today_usd: data.today_usd,
      cap_usd: data.cap_usd,
      headroom_usd: data.headroom_usd,
      pct_used: data.cap_usd > 0 ? Math.round((data.today_usd / data.cap_usd) * 100) : 0,
      by_source: data.by_source,
      by_user: data.by_user,
    },
  ]
  const totalUsd = data.total_usd ?? services.reduce((s, x) => s + x.today_usd, 0)
  const totalCap = data.total_cap_usd ?? services.reduce((s, x) => s + x.cap_usd, 0)
  const totalHeadroom = data.total_headroom_usd ?? Math.max(0, totalCap - totalUsd)

  const pct = totalCap > 0
    ? Math.min(100, Math.max(0, (totalUsd / totalCap) * 100))
    : 0
  // Tier the bar colour so a glance tells you the state: green <50%,
  // yellow 50-80%, red >80%. Threshold values match what we'd alert on
  // if we wire Slack later.
  const tier: 'safe' | 'warn' | 'danger' =
    pct < 50 ? 'safe' : pct < 80 ? 'warn' : 'danger'
  const barColor =
    tier === 'safe' ? '#34D399' : tier === 'warn' ? '#FBBF24' : '#F87171'

  const lastRefresh = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : ''

  return (
    <div className="space-y-5">
      {/* Header row: day picker + last-refresh stamp + cap pill */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-[var(--color-silver)]" />
          <Select value={day ?? '__today__'} onValueChange={(v) => setDay(v === '__today__' ? null : v)}>
            <SelectTrigger className="h-8 w-56 text-xs">
              <SelectValue placeholder="Pick a day" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__today__">Today (live)</SelectItem>
              {dayOptions.slice(1).map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 text-xs text-[var(--color-silver)]">
          {lastRefresh && <span>Last refresh: {lastRefresh}</span>}
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Monthly run rate. The rest of this page is daily usage spend; this
          block is the subscriptions underneath it, so the page answers
          "what does this cost us to run" rather than just "what did the
          models cost today". */}
      {data.costs && <MonthlyCostPanel costs={data.costs} />}

      {/* Top stat row — total = sum of per-user caps, not a hardcoded
          org-wide ceiling. Spend = sum of every user's today spend. */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <Card className="group relative flex-1 overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-[#10B981]/0 blur-2xl transition-all duration-300 group-hover:bg-[#10B981]/15"
          />
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="eyebrow text-[var(--color-silver)]">Total spend today</p>
              <DollarSign className="h-4 w-4 text-[#34D399]" />
            </div>
            <div className="mt-1 text-2xl font-bold tracking-tight text-[var(--color-silver-bright)]">
              {formatUsd(totalUsd)}
            </div>
            <p className="mt-0.5 text-xs text-[var(--color-silver)]">
              {data.day} — sum of every user's API spend
            </p>
          </CardContent>
        </Card>
        <Card className="group relative flex-1 overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="eyebrow text-[var(--color-silver)]">Total daily limit</p>
              <ShieldOff className="h-4 w-4 text-[#34D399]" />
            </div>
            <div className="mt-1 text-2xl font-bold tracking-tight text-[var(--color-silver-bright)]">
              ${totalCap.toFixed(2)}
            </div>
            <p className="mt-0.5 text-xs text-[var(--color-silver)]">
              Sum of every user's per-user API cap. No org-wide ceiling.
            </p>
          </CardContent>
        </Card>
        <Card className="group relative flex-1 overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="eyebrow text-[var(--color-silver)]">Headroom</p>
              {tier === 'danger' ? (
                <AlertTriangle className="h-4 w-4 text-[#F87171]" />
              ) : (
                <Activity className="h-4 w-4 text-[#34D399]" />
              )}
            </div>
            <div className="mt-1 text-2xl font-bold tracking-tight text-[var(--color-silver-bright)]">
              {formatUsd(totalHeadroom)}
            </div>
            <p className="mt-0.5 text-xs text-[var(--color-silver)]">
              {pct.toFixed(1)}% of total cap used
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Overall progress bar */}
      <Card>
        <CardContent className="p-4">
          <div className="mb-2 flex items-center justify-between text-xs">
            <span className="text-[var(--color-silver)]">Total cap usage</span>
            <span className="font-mono text-[var(--color-silver-bright)]">
              {formatUsd(totalUsd)} / ${totalCap.toFixed(2)}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-[rgba(255,255,255,0.06)]">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${pct}%`, backgroundColor: barColor }}
            />
          </div>
          {tier === 'danger' && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-[#F87171]">
              <AlertTriangle className="h-3.5 w-3.5" />
              Above 80% of the total daily limit. Individual users over
              their per-user cap are already refusing AI calls via
              BudgetExceededError.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Per-service grid — one card per provider (Anthropic, OpenAI, Perplexity) */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {services.map((card) => (
          <ServiceCard key={card.service} card={card} />
        ))}
      </div>

      {/* Apollo card — separate because billing is MTD credits, not USD.
          2026-06-07: Added live /v1/auth/health balance alongside ledger MTD,
          plus assigned-quota warning when per-user assignments exceed the
          org-wide cap. */}
      {data.apollo && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-baseline justify-between">
              <CardTitle className="text-sm">Apollo.io enrichment (monthly credits)</CardTitle>
              <div className="text-right">
                {/* Prefer the live remaining from Apollo when available — it's
                    the source of truth. Local ledger is a shadow that can
                    drift if Apollo charged outside our pipeline. */}
                {data.apollo.live && data.apollo.live.credits_remaining !== null ? (
                  <>
                    <div className="font-mono text-sm text-[var(--color-silver-bright)]">
                      {(data.apollo.live.credits_used ?? 0).toLocaleString()} / {(data.apollo.live.credits_limit ?? data.apollo.cap_credits_monthly).toLocaleString()} credits
                      <span className="ml-1 text-[10px] text-[#34D399]">[live]</span>
                    </div>
                    <div className="text-[10px] text-[var(--color-silver)] font-mono">
                      {(data.apollo.live.credits_remaining ?? 0).toLocaleString()} remaining (Apollo)
                      {' · '}{data.apollo.credits_mtd.toLocaleString()} ledger MTD
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-mono text-sm text-[var(--color-silver-bright)]">
                      {data.apollo.credits_mtd.toLocaleString()} / {data.apollo.cap_credits_monthly.toLocaleString()} credits
                    </div>
                    <div className="text-[10px] text-[var(--color-silver)] font-mono">
                      {data.apollo.credits_today.toLocaleString()} today · {data.apollo.headroom_mtd.toLocaleString()} remaining (ledger)
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            {/* Over-allocation warning — fires when SUM(user quotas) > cap.
                Pre-launch we're under-allocated (3 keepers untouched + a
                few Pro signups should not cross 2,500). Post-launch this
                is the upgrade-now trigger. */}
            {data.apollo.assigned_quota_over_cap && (
              <div className="flex items-start gap-2 rounded-md border border-[#F87171]/40 bg-[#F87171]/10 p-2.5 text-xs">
                <AlertTriangle className="h-4 w-4 mt-0.5 text-[#F87171] shrink-0" />
                <div className="text-[var(--color-silver-bright)] leading-snug">
                  <span className="font-medium">Over-allocated:</span> assigned per-user quotas
                  ({(data.apollo.assigned_quota_credits ?? 0).toLocaleString()} credits) exceed the
                  Apollo monthly cap ({data.apollo.cap_credits_monthly.toLocaleString()}). Upgrade the
                  Apollo plan or reduce per-user quotas via{' '}
                  <span className="font-mono">user_settings.apollo_monthly_quota_credits</span>.
                </div>
              </div>
            )}
            <div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-[rgba(255,255,255,0.06)]">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${data.apollo.pct_used}%`,
                    backgroundColor:
                      data.apollo.pct_used < 50 ? '#34D399'
                      : data.apollo.pct_used < 80 ? '#FBBF24'
                      : '#F87171',
                  }}
                />
              </div>
              <div className="mt-1 flex justify-between text-[10px] text-[var(--color-silver)]">
                <span>{data.apollo.pct_used}% of monthly cap used (ledger)</span>
                <span>
                  Assigned: {(data.apollo.assigned_quota_credits ?? 0).toLocaleString()} / {data.apollo.cap_credits_monthly.toLocaleString()}
                  {data.apollo.live?.plan_name && (
                    <span className="ml-1 text-[var(--color-silver)]">· {data.apollo.live.plan_name}</span>
                  )}
                </span>
              </div>
            </div>
            {/* Committed-credit accounting (2026-07-22). Users' unspent credit
                balances are Apollo credits we already owe — each one, when
                spent on enrichment, burns one Apollo credit. "Truly available"
                = cap - used MTD - those balances, and going negative means
                the org has promised more credits than the Apollo plan holds. */}
            {typeof data.apollo.uncommitted_headroom === 'number' && (
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md border border-border px-2 py-1.5">
                  <div className="font-mono text-sm text-[var(--color-silver-bright)]">
                    {data.apollo.credits_mtd.toLocaleString()}
                  </div>
                  <div className="text-[10px] text-[var(--color-silver)]">Used MTD</div>
                </div>
                <div className="rounded-md border border-border px-2 py-1.5">
                  <div className="font-mono text-sm text-[var(--color-silver-bright)]">
                    {(data.apollo.assigned_unspent_credits ?? 0).toLocaleString()}
                  </div>
                  <div className="text-[10px] text-[var(--color-silver)]">Assigned to users (unspent)</div>
                </div>
                <div
                  className={cn(
                    'rounded-md border px-2 py-1.5',
                    data.apollo.uncommitted_headroom < 0
                      ? 'border-[#F87171]/40 bg-[#F87171]/10'
                      : 'border-border',
                  )}
                >
                  <div
                    className={cn(
                      'font-mono text-sm',
                      data.apollo.uncommitted_headroom < 0 ? 'text-[#F87171]' : 'text-[var(--color-silver-bright)]',
                    )}
                  >
                    {data.apollo.uncommitted_headroom.toLocaleString()}
                  </div>
                  <div className="text-[10px] text-[var(--color-silver)]">Truly available (cap - used - assigned)</div>
                </div>
              </div>
            )}
            {typeof data.apollo.uncommitted_headroom === 'number' && data.apollo.uncommitted_headroom < 0 && (
              <div className="flex items-start gap-2 rounded-md border border-[#F87171]/40 bg-[#F87171]/10 p-2.5 text-xs">
                <AlertTriangle className="h-4 w-4 mt-0.5 text-[#F87171] shrink-0" />
                <div className="text-[var(--color-silver-bright)] leading-snug">
                  <span className="font-medium">Over-committed:</span> users hold more unspent credits
                  than the remaining Apollo pool. If everyone spent their balance this month, Apollo
                  would run out {Math.abs(data.apollo.uncommitted_headroom).toLocaleString()} credits
                  short. Upgrade the Apollo plan before onboarding more users.
                </div>
              </div>
            )}
            {data.apollo.by_user.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[var(--color-silver)] mb-1">Top users (MTD)</p>
                <div className="space-y-0.5">
                  {data.apollo.by_user.slice(0, 5).map((row, idx) => (
                    <div key={`${row.user_id ?? 'system'}-${idx}`} className="flex items-center justify-between text-xs">
                      <span className="font-mono text-[var(--color-silver-bright)] truncate">
                        {row.user_id?.slice(0, 24) ?? <span className="text-[var(--color-silver)]">cron</span>}
                      </span>
                      <span className="font-mono text-[var(--color-silver-bright)]">
                        {row.credits.toLocaleString()} credits
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Per-user grand totals + per-user daily table (added 2026-06-07).
          These are the primary view for monitoring a launch — show one card
          per user with their full per-service spend and personal cap. */}
      {data.org_summary && <OrgSummaryCard summary={data.org_summary} />}
      {data.by_user_daily && (
        <PerUserDailyTable
          rows={data.by_user_daily}
          defaultCap={data.org_summary?.default_per_user_cap_usd ?? 2}
        />
      )}

      <p className="text-xs text-[var(--color-silver)]">
        Spend data lives in <span className="font-mono">ai_spend_ledger</span>{' '}
        and <span className="font-mono">apollo_spend_ledger</span>, written by
        the per-service gateways on every billed call. Page auto-refreshes
        every 30 seconds. Total org limit is the SUM of every user's per-user
        Anthropic cap (<span className="font-mono">user_settings.daily_ai_spend_cap_usd</span>);
        new Pro signups get $0.50/day and the default fallback is ${data.org_summary?.default_per_user_cap_usd?.toFixed(2) ?? '5.00'}/day.
        Apollo capped separately at {data.apollo?.cap_credits_monthly?.toLocaleString() ?? '2500'} credits/month.
        Unipile is subscription-billed and not tracked here. See{' '}
        <span className="font-mono">AI_GATEWAY_GUARDRAIL.md</span> for guardrail rationale.
      </p>
    </div>
  )
}

function UserFilter({
  users,
  value,
  onChange,
}: {
  users: AdminUserRow[] | undefined
  value: string | null
  onChange: (v: string | null) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <Filter className="h-3.5 w-3.5 text-[var(--color-silver)]" />
      <Select value={value ?? '__all__'} onValueChange={(v) => onChange(v === '__all__' ? null : v)}>
        <SelectTrigger className="h-8 w-64 text-xs">
          <SelectValue placeholder="Filter by user" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All users</SelectItem>
          {(users ?? []).map((u) => (
            <SelectItem key={u.id} value={u.id}>
              {u.full_name || u.email}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Comp toggle confirmation modal.
//
// Grant copy reminds the staff that flipping the flag does NOT auto-cancel
// the user's existing Stripe sub unless they tick the box (and only works
// when STRIPE_SECRET_KEY is bound on the leads worker — otherwise that part
// silently no-ops and the staff has to cancel in stripe.com instead).
//
// User-facing UI is intentionally unchanged for comp users — they appear as
// a regular Custom seat with no "COMP" or "Friends & Family" branding.
// ---------------------------------------------------------------------------
function CompConfirmDialog({
  user,
  mode,
  onClose,
}: {
  user: AdminUserRow
  mode: 'grant' | 'revoke'
  onClose: () => void
}) {
  const toggle = useToggleComp()
  const hasActiveSub = Boolean(user.stripe_subscription_id)

  const handleConfirm = async () => {
    try {
      const res = await toggle.mutateAsync({ userId: user.id, enabled: mode === 'grant' })
      const msg =
        mode === 'grant'
          ? `Comp granted to ${user.email}. ${hasActiveSub ? 'Reminder: ask them to cancel their Stripe sub themselves so we never auto-charge.' : ''}`
          : `Comp revoked for ${user.email}. Reverted to ${res.reverted_to_plan ?? user.plan}.`
      alert(msg)
      onClose()
    } catch (err) {
      alert(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'grant' ? 'Grant comp access' : 'Revoke comp access'} — {user.email}
          </DialogTitle>
          <DialogDescription className="text-[var(--color-silver)]">
            {mode === 'grant' ? (
              <>
                This user will get full Custom-plan-level access at no charge, and their
                credit balance is topped up to their stored plan&apos;s monthly
                allocation (Basic 100 / Pro 200 — no extra credits beyond the plan).
                Stripe webhooks for their account will be ignored from now on. Their
                stored plan ({user.plan}) is preserved so revoking later reverts
                them cleanly.
              </>
            ) : (
              <>
                This user will lose Custom-plan-level access immediately and revert to
                their stored plan: {user.plan} ({user.plan_status}). They will need
                an active Stripe subscription to continue.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {mode === 'grant' && hasActiveSub && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200/90 leading-relaxed">
            <strong className="font-semibold">Heads up:</strong> this user has an
            active Stripe subscription (
            <code className="text-[10px]">{user.stripe_subscription_id}</code>).
            Flipping comp prevents future plan mutations but does NOT cancel the
            sub — Stripe will keep billing the card on file until cancelled.
            <br />
            Send them the self-cancel steps so they end billing themselves.
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={toggle.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={toggle.isPending}
            className={
              mode === 'grant'
                ? 'bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25'
                : 'bg-red-500/15 text-red-200 hover:bg-red-500/25'
            }
          >
            {toggle.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            {mode === 'grant' ? 'Grant comp access' : 'Revoke comp access'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Staff card: generate + track the single-use "first month free" Stripe
// promotion codes. Redeeming one gives the customer their first month at
// $0 and routes their AI message generation to the Basic-tier model for
// that month (even on Pro); the plan's limits and features are unchanged.
function FreeMonthCodesCard() {
  const { data: codes, isLoading } = useFreeMonthCodes()
  const generate = useGenerateFreeMonthCode()
  const [customCode, setCustomCode] = useState('')
  const [lastCode, setLastCode] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  function handleGenerate() {
    generate.mutate(
      { code: customCode.trim() || undefined },
      {
        onSuccess: (data) => {
          setLastCode(data.code)
          setCustomCode('')
          setCopied(false)
        },
      },
    )
  }

  function handleCopy(code: string) {
    void navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <Gift className="h-4 w-4 text-[#34D399]" />
          Free Month Codes
        </CardTitle>
        <p className="text-xs text-[var(--color-silver)]">
          Single-use Stripe promo codes: first month free, message generation on the
          Basic model for that month (even on Pro). Customer enters the code in the
          Stripe checkout page.
        </p>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={customCode}
            onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
            placeholder="Custom code (optional, e.g. ELIFREE1)"
            className="h-8 max-w-[260px] text-xs"
            maxLength={32}
          />
          <Button size="sm" className="h-8" onClick={handleGenerate} disabled={generate.isPending}>
            {generate.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Generate code
          </Button>
          {lastCode && (
            <button
              type="button"
              onClick={() => handleCopy(lastCode)}
              className="flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 font-mono text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20"
              title="Copy code"
            >
              {lastCode}
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            </button>
          )}
        </div>
        {generate.isError && (
          <p className="text-xs text-red-400">
            {generate.error instanceof Error ? generate.error.message : 'Failed to generate code.'}
          </p>
        )}
        {isLoading && <p className="text-xs text-[var(--color-silver)]">Loading codes…</p>}
        {!isLoading && codes && codes.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {codes.map((c) => {
              const redeemed = c.max_redemptions !== null && c.times_redeemed >= c.max_redemptions
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleCopy(c.code)}
                  title={`Created ${new Date(c.created_at).toLocaleDateString()} — click to copy`}
                  className={cn(
                    'rounded-md border px-2 py-0.5 font-mono text-[11px]',
                    redeemed || !c.active
                      ? 'border-border text-subtle-foreground line-through'
                      : 'border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/10',
                  )}
                >
                  {c.code}
                  {redeemed ? ' (used)' : ''}
                </button>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function UsersPage() {
  const { user: viewer } = useUser()
  // Team console (/api/team) reuses this table read-only: no promo codes,
  // comp or impersonation, and the column shows the rep's team role.
  const isTeam = useAdminApiBase() === '/api/team'
  // QA can browse users but not impersonate or change comp (backend enforces too).
  const canAct = isStaffUser(viewer?.id) && !isTeam
  const { data: users, isLoading } = useAdminUsers()
  const [search, setSearch] = useState('')
  const [compTarget, setCompTarget] = useState<{ user: AdminUserRow; mode: 'grant' | 'revoke' } | null>(null)

  const filtered = useMemo(() => {
    if (!users) return []
    const q = search.toLowerCase().trim()
    if (!q) return users
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.full_name ?? '').toLowerCase().includes(q) ||
        (u.linkedin_name ?? '').toLowerCase().includes(q),
    )
  }, [users, search])

  return (
    <div className="space-y-3">
      {!isTeam && <FreeMonthCodesCard />}
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search email / name / LinkedIn…"
        className="h-9 max-w-sm"
      />
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Billing</TableHead>
                <TableHead>LinkedIn</TableHead>
                <TableHead className="text-right">Leads</TableHead>
                <TableHead className="text-right">Campaigns</TableHead>
                <TableHead className="text-right">Sent</TableHead>
                <TableHead className="text-right">Replies</TableHead>
                <TableHead>Last activity</TableHead>
                <TableHead className="w-[40px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-sm text-[var(--color-silver)] py-6">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && filtered.map((u) => {
                const isComp = u.is_comp === 1
                return (
                  <TableRow key={u.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-[var(--color-silver-bright)]">
                          {u.full_name || '—'}
                        </span>
                        <span className="text-xs text-[var(--color-silver)]">{u.email}</span>
                        {isTeam && u.role && (
                          <span className="text-[10px] uppercase tracking-wider text-[#34D399]">{u.role}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {/* Comp accounts resolve to Pro everywhere — show that as the
                          plan. The dedicated COMP badge in the next column (with its
                          grant tooltip) still marks it as complimentary. */}
                      <Badge className="bg-[rgba(255,255,255,0.05)] text-[var(--color-silver-bright)] capitalize">
                        {u.is_comp === 1 ? 'pro' : u.plan}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {isComp ? (
                        <Badge
                          className="bg-[rgba(168,85,247,0.14)] text-[#D8B4FE] border-[rgba(168,85,247,0.3)] gap-1"
                          title={
                            u.comp_granted_at
                              ? `Granted by ${u.comp_granted_by ?? '?'} on ${fmtLocale(u.comp_granted_at, 'date')}`
                              : 'Friends & Family override'
                          }
                        >
                          <Gift className="h-3 w-3" />
                          COMP
                        </Badge>
                      ) : (
                        <Badge
                          className={cn(
                            'capitalize text-[10px]',
                            u.plan_status === 'active' && 'bg-emerald-500/12 text-emerald-300',
                            u.plan_status === 'past_due' && 'bg-amber-500/12 text-amber-300',
                            u.plan_status === 'cancelled' && 'bg-[rgba(255,255,255,0.05)] text-[var(--color-silver)]',
                            u.plan_status === 'waitlisted' && 'bg-sky-500/12 text-sky-300',
                          )}
                        >
                          {u.plan_status}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.linkedin_name ? (
                        <span className="text-xs text-emerald-300">{u.linkedin_name}</span>
                      ) : (
                        <span className="text-xs text-[var(--color-silver)]">Not connected</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{u.leads_count}</TableCell>
                    <TableCell className="text-right tabular-nums">{u.campaigns_count}</TableCell>
                    <TableCell className="text-right tabular-nums">{u.sent_count}</TableCell>
                    <TableCell className="text-right tabular-nums">{u.received_count}</TableCell>
                    <TableCell className="text-xs text-[var(--color-silver)]">
                      {fmtLocale(u.last_activity_at)}
                    </TableCell>
                    <TableCell>
                      {canAct && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setImpersonatedUser(u.id, u.email)
                              // Hard navigation resets every react-query cache so
                              // the whole app re-renders scoped to this user.
                              window.location.href = '/'
                            }}
                          >
                            Open dashboard as this user
                          </DropdownMenuItem>
                          {isComp ? (
                            <DropdownMenuItem onClick={() => setCompTarget({ user: u, mode: 'revoke' })}>
                              Revoke comp access
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => setCompTarget({ user: u, mode: 'grant' })}>
                              Grant comp access
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {compTarget && (
        <CompConfirmDialog
          user={compTarget.user}
          mode={compTarget.mode}
          onClose={() => setCompTarget(null)}
        />
      )}
    </div>
  )
}

export function LeadsPage() {
  const [filterUser, setFilterUser] = useState<string | null>(null)
  const { data: users } = useAdminUsers()
  const { data: leads, isLoading } = useAdminLeads(filterUser)
  return (
    <div className="space-y-3">
      <UserFilter users={users} value={filterUser} onChange={setFilterUser} />
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lead</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">ICP</TableHead>
                <TableHead>Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-[var(--color-silver)] py-6">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && (leads ?? []).map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="font-medium text-[var(--color-silver-bright)]">
                    {l.full_name ?? `${l.first_name ?? ''} ${l.last_name ?? ''}`.trim() ?? '—'}
                    {l.title && (
                      <div className="text-xs text-[var(--color-silver)]">{l.title}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{l.company ?? '—'}</TableCell>
                  <TableCell className="text-xs text-[var(--color-silver)]">{l.owner_email}</TableCell>
                  <TableCell>
                    <Badge className="bg-[rgba(255,255,255,0.05)] text-[var(--color-silver-bright)] text-[10px]">
                      {l.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{l.icp_score ?? '—'}</TableCell>
                  <TableCell className="text-xs text-[var(--color-silver)]">
                    {fmtLocale(l.created_at, 'date')}
                  </TableCell>
                  <TableCell>
                    <a href={l.linkedin_url} target="_blank" rel="noreferrer" className="text-[#34D399] hover:underline">
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

export function CampaignsPage() {
  const [filterUser, setFilterUser] = useState<string | null>(null)
  const { data: users } = useAdminUsers()
  const { data: campaigns, isLoading } = useAdminCampaigns(filterUser)
  return (
    <div className="space-y-3">
      <UserFilter users={users} value={filterUser} onChange={setFilterUser} />
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Leads</TableHead>
                <TableHead className="text-right">Sent</TableHead>
                <TableHead className="text-right">Replied</TableHead>
                <TableHead className="text-right">Meetings</TableHead>
                <TableHead className="text-right">Daily</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-sm text-[var(--color-silver)] py-6">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && (campaigns ?? []).map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium text-[var(--color-silver-bright)]">
                    {c.name}
                  </TableCell>
                  <TableCell className="text-xs text-[var(--color-silver)]">{c.owner_email}</TableCell>
                  <TableCell>
                    <Badge
                      className={cn(
                        'text-[10px]',
                        c.status === 'active' && 'bg-emerald-500/12 text-emerald-300',
                        c.status === 'paused' && 'bg-amber-500/12 text-amber-300',
                        c.status === 'draft' && 'bg-[rgba(255,255,255,0.05)] text-[var(--color-silver)]',
                      )}
                    >
                      {c.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{c.leads_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{c.stats_sent}</TableCell>
                  <TableCell className="text-right tabular-nums">{c.stats_replied}</TableCell>
                  <TableCell className="text-right tabular-nums">{c.stats_meetings}</TableCell>
                  <TableCell className="text-right tabular-nums">{c.daily_limit}</TableCell>
                  <TableCell className="text-xs text-[var(--color-silver)]">
                    {fmtLocale(c.updated_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

export function ContentPage() {
  const [filterUser, setFilterUser] = useState<string | null>(null)
  const { data: users } = useAdminUsers()
  const { data: posts, isLoading } = useAdminContent(filterUser)
  return (
    <div className="space-y-3">
      <UserFilter users={users} value={filterUser} onChange={setFilterUser} />
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Post</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Likes</TableHead>
                <TableHead className="text-right">Comments</TableHead>
                <TableHead>Scheduled / Published</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-[var(--color-silver)] py-6">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && (posts ?? []).map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="max-w-md">
                    <div className="text-sm text-[var(--color-silver-bright)] line-clamp-2">
                      {p.content}
                    </div>
                    {p.topic && (
                      <div className="text-xs text-[var(--color-silver)] mt-0.5">{p.topic}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-[var(--color-silver)]">{p.owner_email}</TableCell>
                  <TableCell>
                    <Badge
                      className={cn(
                        'text-[10px]',
                        p.status === 'published' && 'bg-emerald-500/12 text-emerald-300',
                        p.status === 'scheduled' && 'bg-[rgba(16,185,129,0.12)] text-[#34D399]',
                        p.status === 'draft' && 'bg-[rgba(255,255,255,0.05)] text-[var(--color-silver)]',
                      )}
                    >
                      {p.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{p.metrics_likes}</TableCell>
                  <TableCell className="text-right tabular-nums">{p.metrics_comments}</TableCell>
                  <TableCell className="text-xs text-[var(--color-silver)]">
                    {p.published_at
                      ? `Published ${fmtLocale(p.published_at)}`
                      : p.scheduled_for
                      ? `For ${fmtLocale(p.scheduled_for)}`
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

export function InboxPage() {
  const [filterUser, setFilterUser] = useState<string | null>(null)
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null)
  const { data: users } = useAdminUsers()
  const { data: convos, isLoading } = useAdminInbox(filterUser)
  const { data: messages } = useAdminInboxMessages(selectedLeadId)

  return (
    <div className="space-y-3">
      <UserFilter users={users} value={filterUser} onChange={setFilterUser} />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[2fr_3fr]">
        <Card>
          <CardContent className="p-0">
            <ScrollArea className="h-[60vh]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lead</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead className="text-right">Msgs</TableHead>
                    <TableHead>Last</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-sm text-[var(--color-silver)] py-6">
                        Loading…
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && (convos ?? []).map((c) => (
                    <TableRow
                      key={`${c.user_id}_${c.lead_id}`}
                      onClick={() => setSelectedLeadId(c.lead_id)}
                      className={cn(
                        'cursor-pointer',
                        selectedLeadId === c.lead_id && 'bg-[rgba(16,185,129,0.08)]',
                      )}
                    >
                      <TableCell>
                        <div className="text-sm font-medium text-[var(--color-silver-bright)]">
                          {c.lead_name ?? '—'}
                        </div>
                        <div className="text-xs text-[var(--color-silver)]">{c.lead_company ?? ''}</div>
                      </TableCell>
                      <TableCell className="text-xs text-[var(--color-silver)]">{c.owner_email}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs">
                        <span className="text-emerald-300">↓{c.inbound_count}</span>
                        {' / '}
                        <span className="text-[#34D399]">↑{c.outbound_count}</span>
                      </TableCell>
                      <TableCell className="text-xs text-[var(--color-silver)]">
                        {fmtLocale(c.last_message_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {selectedLeadId ? 'Conversation thread' : 'Select a conversation to view'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[55vh]">
              {!selectedLeadId && (
                <p className="text-xs text-[var(--color-silver)]">
                  Click any row on the left to inspect its full message thread.
                </p>
              )}
              <div className="space-y-3">
                {(messages ?? []).map((m) => (
                  <div
                    key={m.id}
                    className={cn(
                      'rounded-lg border p-3',
                      m.direction === 'inbound'
                        ? 'border-border bg-[rgba(255,255,255,0.02)]'
                        : 'border-[rgba(16,185,129,0.25)] bg-[rgba(16,185,129,0.06)]',
                    )}
                  >
                    <div className="flex items-center justify-between text-xs text-[var(--color-silver)] mb-1">
                      <span>
                        <Badge
                          className={cn(
                            'text-[10px] mr-1.5',
                            m.direction === 'inbound'
                              ? 'bg-[rgba(255,255,255,0.05)] text-[var(--color-silver)]'
                              : 'bg-[rgba(16,185,129,0.18)] text-[#34D399]',
                          )}
                        >
                          {m.direction}
                        </Badge>
                        <span className="font-mono">{m.message_type}</span>
                        {m.status && <span className="ml-1.5">· {m.status}</span>}
                      </span>
                      <span>
                        {new Date(m.sent_at ?? m.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap text-[var(--color-silver-bright)]">
                      {m.content}
                    </p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export function SuperAdminApp() {
  const { user, isLoaded } = useUser()
  const location = useLocation()

  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center text-[var(--color-silver)]">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
      </div>
    )
  }

  if (!canViewAdminConsole(user?.id)) {
    return <NotStaff />
  }
  const readOnly = !isStaffUser(user?.id)

  return (
    <>
      <style>{GREEN_THEME_STYLE}</style>
      <div className="super-admin relative min-h-screen overflow-x-hidden bg-background">
        <MatrixRain variant="green" />
        <div className="relative z-10 mx-auto max-w-7xl p-6 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="eyebrow text-[#34D399]">// SUPER ADMIN</p>
              <h1 className="text-3xl font-bold tracking-tight text-[var(--color-silver-bright)]">
                DealCloser Staff Console
              </h1>
              <p className="text-sm text-[var(--color-silver)] mt-1">
                Cross-user view of every customer's leads, campaigns, content, and inboxes.
              </p>
              {readOnly && (
                <p className="mt-2 inline-block rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs text-amber-300">
                  QA read-only access: you can browse everything here, but changes (comp, caps, replies, links, requeues) are disabled.
                </p>
              )}
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/dashboard">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Customer dashboard
              </Link>
            </Button>
          </div>

          <AdminNav />

          <Routes location={location}>
            <Route index element={<OverviewPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="teams" element={<TeamsPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="campaigns" element={<CampaignsPage />} />
            <Route path="content" element={<ContentPage />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="orchestrator" element={<OrchestratorPage />} />
            <Route path="message-audit" element={<MessageAuditPage />} />
            <Route path="message-insights" element={<MessageInsightsPage />} />
            <Route path="mcp" element={<McpPage />} />
            <Route path="lifecycle" element={<LifecyclePage />} />
            <Route path="seo" element={<SeoPage />} />
            <Route path="links" element={<LinksPage />} />
            <Route path="custom-plans" element={<CustomPlansPage />} />
            <Route path="spend" element={<AISpendPage />} />
            <Route path="support" element={<AdminSupportPage />} />
            <Route path="skills" element={<SkillsLabPage />} />
            <Route path="*" element={<Navigate to="/super-admin" replace />} />
          </Routes>
        </div>
      </div>
    </>
  )
}
